/**
 * Cross-platform image picker utility
 * Works on both web and mobile platforms
 */
import * as ImagePicker from 'expo-image-picker';
import { Alert, Platform } from 'react-native';
import { toast } from '@/components/ui/toast';

export interface ImagePickerResult {
  uri: string;
  type?: string;
  name?: string;
  cancelled: boolean;
  /** Original File reference (web only). Retained to avoid stale blob URL re-fetches. */
  file?: File;
}

export interface ImagePickerOptions {
  allowsEditing?: boolean;
  aspect?: [number, number];
  quality?: number;
  /** Web only: show advanced crop modal with free-form crop, rotation, and skip option */
  showAdvancedCrop?: boolean;
}

/**
 * Pick an image from the gallery/file system
 */
export async function pickImageFromGallery(options: ImagePickerOptions = {}): Promise<ImagePickerResult> {
  const {
    allowsEditing = true,
    aspect = [1, 1],
    quality = 0.8,
    showAdvancedCrop,
  } = options;

  if (Platform.OS === 'web') {
    return pickImageFromWeb(allowsEditing ? aspect : undefined, showAdvancedCrop);
  } else {
    // Mobile implementation
    const { status: libraryStatus } = await ImagePicker.requestMediaLibraryPermissionsAsync();
    if (libraryStatus !== 'granted') {
      throw new Error('Photo library permission is required to select images.');
    }

    const result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ImagePicker.MediaTypeOptions.Images,
      allowsEditing,
      ...(allowsEditing && aspect ? { aspect } : {}),
      quality,
    });

    if (result.canceled || !result.assets || result.assets.length === 0) {
      return { uri: '', cancelled: true };
    }

    const asset = result.assets[0];
    return {
      uri: asset.uri,
      type: asset.mimeType,
      name: asset.fileName || 'image.jpg',
      cancelled: false
    };
  }
}

/**
 * Take a photo using the camera
 */
export async function takePhotoWithCamera(options: ImagePickerOptions = {}): Promise<ImagePickerResult> {
  const {
    allowsEditing = true,
    aspect = [1, 1],
    quality = 0.8,
    showAdvancedCrop,
  } = options;

  if (Platform.OS === 'web') {
    // On web, fallback to file picker since camera access is complex
    console.log('Camera not available on web, falling back to file picker');
    return pickImageFromWeb(allowsEditing ? aspect : undefined, showAdvancedCrop);
  } else {
    // Mobile implementation
    const { status: cameraStatus } = await ImagePicker.requestCameraPermissionsAsync();
    if (cameraStatus !== 'granted') {
      throw new Error('Camera permission is required to take photos.');
    }

    const result = await ImagePicker.launchCameraAsync({
      mediaTypes: ImagePicker.MediaTypeOptions.Images,
      allowsEditing,
      ...(allowsEditing && aspect ? { aspect } : {}),
      quality,
    });

    if (result.canceled || !result.assets || result.assets.length === 0) {
      return { uri: '', cancelled: true };
    }

    const asset = result.assets[0];
    return {
      uri: asset.uri,
      type: asset.mimeType,
      name: asset.fileName || 'photo.jpg',
      cancelled: false
    };
  }
}

/**
 * Web-specific image picker using HTML file input + crop modal
 */
async function pickImageFromWeb(
  aspect?: [number, number],
  showAdvancedCrop?: boolean
): Promise<ImagePickerResult> {
  const rawResult = await selectFileFromWeb();
  if (rawResult.cancelled) {
    return rawResult;
  }

  // Advanced crop modal (check-in photos): free-form, rotation, skip option
  if (showAdvancedCrop) {
    const { showCheckInCropModal } = await import('@/utils/show-check-in-crop-modal');
    const result = await showCheckInCropModal(rawResult.uri);

    if (result.action === 'cancelled') {
      URL.revokeObjectURL(rawResult.uri);
      return { uri: '', cancelled: true };
    }

    if (result.action === 'skipped') {
      return rawResult;
    }

    // result.action === 'cropped'
    URL.revokeObjectURL(rawResult.uri);

    let croppedFile: File | undefined;
    try {
      const resp = await fetch(result.blobUri);
      const blob = await resp.blob();
      croppedFile = new File([blob], rawResult.name || 'photo.jpg', {
        type: rawResult.type || 'image/jpeg',
      });
    } catch {
      // Fallback: upload path will re-fetch the blob URL if needed
    }

    return {
      uri: result.blobUri,
      type: rawResult.type,
      name: rawResult.name,
      cancelled: false,
      file: croppedFile,
    };
  }

  // Skip crop modal when no aspect ratio is specified (allowsEditing: false)
  if (!aspect) {
    return rawResult;
  }

  // Dynamically import to avoid loading react-easy-crop at module parse time
  const { showCropModal } = await import('@/utils/show-crop-modal');
  const aspectRatio = aspect[0] / aspect[1];
  const croppedUri = await showCropModal(rawResult.uri, aspectRatio);

  // Revoke the original blob URI regardless of outcome
  URL.revokeObjectURL(rawResult.uri);

  if (!croppedUri) {
    return { uri: '', cancelled: true };
  }

  // Convert the cropped blob URL to a File so we retain a stable reference
  let croppedFile: File | undefined;
  try {
    const resp = await fetch(croppedUri);
    const blob = await resp.blob();
    croppedFile = new File([blob], rawResult.name || 'photo.jpg', {
      type: rawResult.type || 'image/jpeg',
    });
  } catch {
    // Fallback: upload path will re-fetch the blob URL if needed
  }

  return {
    uri: croppedUri,
    type: rawResult.type,
    name: rawResult.name,
    cancelled: false,
    file: croppedFile,
  };
}

/**
 * Low-level file selection via HTML file input (no cropping).
 */
function selectFileFromWeb(): Promise<ImagePickerResult> {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/jpeg,image/png,image/webp';
    input.style.display = 'none';

    input.onchange = (event) => {
      const target = event.target as HTMLInputElement;
      const files = target.files;

      if (!files || files.length === 0) {
        resolve({ uri: '', cancelled: true });
        return;
      }

      const file = files[0];

      if (!file.type.startsWith('image/')) {
        console.error('Selected file is not an image');
        resolve({ uri: '', cancelled: true });
        return;
      }

      const maxSize = 10 * 1024 * 1024; // 10MB
      if (file.size > maxSize) {
        console.error('File too large. Maximum size is 10MB');
        toast.warning('File too large', { description: 'Please select an image smaller than 10MB.' });
        resolve({ uri: '', cancelled: true });
        return;
      }

      const objectUrl = URL.createObjectURL(file);

      resolve({
        uri: objectUrl,
        type: file.type,
        name: file.name,
        cancelled: false,
        file,
      });

      document.body.removeChild(input);
    };

    input.oncancel = () => {
      resolve({ uri: '', cancelled: true });
      document.body.removeChild(input);
    };

    document.body.appendChild(input);
    input.click();
  });
}

/**
 * Show image picker options (Camera or Gallery)
 */
export function showImagePickerOptions(
  onCameraPress: () => void,
  onGalleryPress: () => void,
  onCancel?: () => void
): void {
  if (Platform.OS === 'web') {
    // On web, just show gallery picker since camera is complex
    onGalleryPress();
  } else {
    // On mobile, show the native alert
    Alert.alert(
      'Select Image',
      'Choose how you want to select your profile image',
      [
        {
          text: 'Camera',
          onPress: onCameraPress,
        },
        {
          text: 'Gallery',
          onPress: onGalleryPress,
        },
        {
          text: 'Cancel',
          style: 'cancel',
          onPress: onCancel,
        },
      ],
      { cancelable: true }
    );
  }
}
